import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  CalendarDays,
  ExternalLink,
  Film,
  ImagePlus,
  Save,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { useAbarytDate } from "../context/AbarytDateContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YAH_SCRIPTURES_URL = "https://yahscriptures.online/";

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

async function csrfConfig() {
  let token = getCookie("ysq_csrf");
  if (!token) {
    const response = await axios.get(`${API}/auth/csrf`, { withCredentials: true });
    token = response.data?.csrf_token || getCookie("ysq_csrf");
  }
  return {
    withCredentials: true,
    headers: token ? { "X-CSRF-Token": token } : {},
  };
}

const editableKeys = [
  "reference",
  "source",
  "text",
  "source_url",
  "theme",
  "appointed_time",
  "devotional_title",
  "devotional_body",
  "encouragement",
  "palal",
  "video_url",
  "video_title",
  "video_poster_url",
];

export default function DevotionalPlanner() {
  const { date, fallbackDate } = useAbarytDate();
  const effectiveDate = date || fallbackDate;
  const currentDay = effectiveDate?.dayOfYear || 1;

  const [schedule, setSchedule] = useState([]);
  const [selectedDay, setSelectedDay] = useState(currentDay);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [manualRecords, setManualRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const start = Math.max(1, currentDay - 2);
      const [scheduleResponse, recordsResponse] = await Promise.all([
        axios.get(`${API}/devotional-planner/schedule`, {
          params: { start_day_of_year: start, days: 60 },
          withCredentials: true,
        }),
        axios.get(`${API}/admin/daily-scriptures`, { withCredentials: true }),
      ]);
      setSchedule(scheduleResponse.data || []);
      setManualRecords(recordsResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load devotional planner");
    } finally {
      setLoading(false);
    }
  }, [currentDay]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const entry = schedule.find((item) => Number(item.day_of_year) === Number(selectedDay)) || schedule[0] || null;
    setSelected(entry);
    if (entry) {
      const manual = manualRecords.find(
        (record) => Number(record.month) === Number(entry.month) && Number(record.day) === Number(entry.day)
      );
      setForm({
        month: entry.month,
        day: entry.day,
        reference: manual?.reference || entry.reference || "",
        source: manual?.source || entry.source || "YAH Scriptures",
        text: manual?.text || entry.text || "",
        source_url: manual?.source_url || entry.source_url || YAH_SCRIPTURES_URL,
        theme: manual?.theme || entry.theme || "",
        appointed_time: manual?.appointed_time || entry.appointed_time || "",
        devotional_title: manual?.devotional_title || entry.devotional_title || "",
        devotional_body: manual?.devotional_body || entry.devotional_body || "",
        encouragement: manual?.encouragement || entry.encouragement || "",
        palal: manual?.palal || entry.palal || "",
        video_url: manual?.video_url || entry.video_url || "",
        video_media_id: manual?.video_media_id || entry.video_media_id || "",
        video_title: manual?.video_title || entry.video_title || "",
        video_poster_url: manual?.video_poster_url || entry.video_poster_url || "",
        video_poster_media_id: manual?.video_poster_media_id || entry.video_poster_media_id || "",
        is_active: manual?.is_active !== false,
      });
    }
  }, [schedule, manualRecords, selectedDay]);

  const manualForSelected = useMemo(() => {
    if (!selected) return null;
    return manualRecords.find(
      (record) => Number(record.month) === Number(selected.month) && Number(record.day) === Number(selected.day)
    ) || null;
  }, [manualRecords, selected]);

  const uploadMedia = async (file, kind) => {
    if (!file || !selected) return;
    const setUploading = kind === "video" ? setUploadingVideo : setUploadingPoster;
    setUploading(true);
    try {
      const config = await csrfConfig();
      const body = new FormData();
      body.append("file", file);
      const response = await axios.post(
        `${API}/admin/devotional-media/upload`,
        body,
        {
          ...config,
          params: { kind, day_of_year: selected.day_of_year },
          headers: {
            ...(config.headers || {}),
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (kind === "video") {
        setForm((current) => ({
          ...current,
          video_url: response.data?.url || "",
          video_media_id: response.data?.id || "",
          video_title: current.video_title || file.name.replace(/\.[^.]+$/, ""),
        }));
      } else {
        setForm((current) => ({
          ...current,
          video_poster_url: response.data?.url || "",
          video_poster_media_id: response.data?.id || "",
        }));
      }

      toast.success(kind === "video" ? "Video uploaded" : "Poster image uploaded");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = async (kind) => {
    const mediaId = kind === "video" ? form?.video_media_id : form?.video_poster_media_id;
    if (!mediaId) {
      setForm((current) => ({
        ...current,
        ...(kind === "video"
          ? { video_url: "", video_media_id: "" }
          : { video_poster_url: "", video_poster_media_id: "" }),
      }));
      return;
    }

    try {
      const config = await csrfConfig();
      await axios.delete(`${API}/admin/devotional-media/${mediaId}`, config);
      setForm((current) => ({
        ...current,
        ...(kind === "video"
          ? { video_url: "", video_media_id: "" }
          : { video_poster_url: "", video_poster_media_id: "" }),
      }));
      toast.success(kind === "video" ? "Video removed" : "Poster removed");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove media");
    }
  };

  const save = async () => {
    if (!selected || !form) return;
    try {
      const config = await csrfConfig();
      const payload = {
        month: Number(selected.month),
        day: Number(selected.day),
        is_active: form.is_active !== false,
      };
      editableKeys.forEach((key) => {
        payload[key] = (form[key] || "").trim();
      });

      if (manualForSelected?.id) {
        await axios.patch(
          `${API}/admin/daily-scriptures/${manualForSelected.id}`,
          payload,
          config
        );
      } else {
        await axios.post(`${API}/admin/daily-scriptures`, payload, config);
      }

      toast.success("Devotional schedule saved");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save devotional");
    }
  };

  if (loading && !schedule.length) {
    return <div className="min-h-[60vh] flex items-center justify-center bg-parchment text-slate-500">Loading planner…</div>;
  }

  return (
    <div className="min-h-screen bg-parchment" data-testid="devotional-planner-page">
      <section className="bg-royal-blue text-white border-b-4 border-kingdom-gold">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-7 sm:py-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to="/portal" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-kingdom-gold">
              <ArrowLeft className="w-4 h-4" /> Account Portal
            </Link>
            <Link to={`/daily-devotional/${selectedDay}`} className="inline-flex items-center gap-2 text-sm font-semibold text-kingdom-gold">
              Preview public devotional <ExternalLink className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-6">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-kingdom-gold">Content planning</p>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl">Daily Word & Devotional Planner</h1>
            <p className="mt-3 max-w-3xl text-white/80">
              Review the upcoming Scripture queue, edit the automatic devotional draft, attach the day’s video,
              and prepare encouragement and palal before publication.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-7 grid lg:grid-cols-[330px_1fr] gap-6">
        <aside className="bg-white border border-slate-200 lg:sticky lg:top-28 lg:self-start">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-2 text-royal-blue">
              <CalendarDays className="w-5 h-5 text-kingdom-gold" />
              <h2 className="font-heading text-xl">Upcoming queue</h2>
            </div>
            <p className="mt-1 text-xs text-slate-500">Today + the next scheduled days.</p>
          </div>

          <div className="max-h-[72vh] overflow-y-auto divide-y divide-slate-100">
            {schedule.map((item) => (
              <button
                key={item.day_of_year}
                type="button"
                onClick={() => setSelectedDay(item.day_of_year)}
                className={`w-full text-left p-4 hover:bg-slate-50 ${Number(item.day_of_year) === Number(selectedDay) ? "bg-kingdom-gold/10" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    M{item.month} · D{item.day}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-royal-blue">
                    {item.is_shabat ? "Shabat" : item.context?.replaceAll("_", " ")}
                  </span>
                </div>
                <div className="mt-1 font-semibold text-royal-blue">{item.reference}</div>
                <div className="mt-1 text-xs text-slate-600 line-clamp-2">{item.devotional_title || item.theme}</div>
                {item.video_url && <div className="mt-2 text-[10px] font-bold text-kingdom-gold">VIDEO READY</div>}
              </button>
            ))}
          </div>
        </aside>

        <main>
          {selected && form ? (
            <div className="space-y-5">
              <section className="bg-white border border-slate-200 p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-kingdom-gold">
                      M{selected.month} · D{selected.day} · Year 5926
                    </p>
                    <h2 className="mt-2 font-heading text-2xl text-royal-blue">
                      {selected.is_shabat ? "Shabat · " : ""}{selected.appointed_time || selected.context?.replaceAll("_", " ")}
                    </h2>
                    {selected.gregorian_date && <p className="mt-1 text-sm text-slate-500">{selected.gregorian_date}</p>}
                  </div>
                  <div className="text-xs font-semibold text-slate-500">
                    {manualForSelected ? "Manual override" : "Automatic draft"}
                  </div>
                </div>
              </section>

              <section className="bg-white border border-slate-200 p-5 sm:p-6 space-y-5">
                <div className="flex items-center gap-2 text-royal-blue">
                  <BookOpen className="w-5 h-5 text-kingdom-gold" />
                  <h2 className="font-heading text-xl">Scripture</h2>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Reference</Label>
                    <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                  </div>
                  <div>
                    <Label>Source</Label>
                    <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>YAH Scriptures link</Label>
                    <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Theme</Label>
                    <Input value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Appointed-time label</Label>
                    <Input value={form.appointed_time} onChange={(e) => setForm({ ...form, appointed_time: e.target.value })} />
                  </div>
                </div>

                <div>
                  <Label>Displayed Scripture text</Label>
                  <textarea
                    className="mt-1 w-full min-h-32 rounded-md border border-input bg-background px-3 py-2 text-base"
                    value={form.text}
                    onChange={(e) => setForm({ ...form, text: e.target.value })}
                  />
                </div>
              </section>

              <section className="bg-white border border-slate-200 p-5 sm:p-6 space-y-5">
                <div className="flex items-center gap-2 text-royal-blue">
                  <Film className="w-5 h-5 text-kingdom-gold" />
                  <h2 className="font-heading text-xl">Daily video</h2>
                </div>

                <div>
                  <Label>Video title</Label>
                  <Input value={form.video_title} onChange={(e) => setForm({ ...form, video_title: e.target.value })} />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-dashed border-royal-blue/40 bg-royal-blue/5 p-4">
                    <div className="flex items-center gap-2 text-royal-blue">
                      <Upload className="h-5 w-5" />
                      <span className="font-semibold">Upload video file</span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">MP4, WebM, MOV, or M4V · up to 250 MB.</p>
                    <input
                      id="daily-video-upload"
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
                      className="mt-3 block w-full text-sm"
                      disabled={uploadingVideo}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) uploadMedia(file, "video");
                        event.target.value = "";
                      }}
                    />
                    {uploadingVideo && <p className="mt-2 text-sm font-semibold text-royal-blue">Uploading video…</p>}
                  </div>

                  <div className="rounded-lg border border-dashed border-kingdom-gold/60 bg-kingdom-gold/5 p-4">
                    <div className="flex items-center gap-2 text-royal-blue">
                      <ImagePlus className="h-5 w-5" />
                      <span className="font-semibold">Upload poster image</span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">JPG, PNG, or WebP · up to 12 MB.</p>
                    <input
                      id="daily-video-poster-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="mt-3 block w-full text-sm"
                      disabled={uploadingPoster}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) uploadMedia(file, "poster");
                        event.target.value = "";
                      }}
                    />
                    {uploadingPoster && <p className="mt-2 text-sm font-semibold text-royal-blue">Uploading poster…</p>}
                  </div>
                </div>

                <div>
                  <Label>Hosted video URL</Label>
                  <Input value={form.video_url} placeholder="https://..." onChange={(e) => setForm({ ...form, video_url: e.target.value, video_media_id: "" })} />
                  <p className="mt-1 text-xs text-slate-500">Upload a file above, or paste an externally hosted video URL.</p>
                </div>

                <div>
                  <Label>Poster image URL</Label>
                  <Input value={form.video_poster_url} placeholder="https://..." onChange={(e) => setForm({ ...form, video_poster_url: e.target.value, video_poster_media_id: "" })} />
                  <p className="mt-1 text-xs text-slate-500">Upload a poster above, or paste an externally hosted image URL.</p>
                </div>

                {form.video_url && (
                  <div className="space-y-3">
                    <video className="w-full max-h-[420px] bg-black" controls playsInline poster={form.video_poster_url || undefined} src={form.video_url} />
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" onClick={() => removeMedia("video")}>
                        <Trash2 className="mr-2 h-4 w-4" /> Remove video
                      </Button>
                      {form.video_poster_url && (
                        <Button type="button" variant="outline" onClick={() => removeMedia("poster")}>
                          <Trash2 className="mr-2 h-4 w-4" /> Remove poster
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </section>

              <section className="bg-white border border-slate-200 p-5 sm:p-6 space-y-5">
                <div className="flex items-center gap-2 text-royal-blue">
                  <Sparkles className="w-5 h-5 text-kingdom-gold" />
                  <h2 className="font-heading text-xl">Devotional</h2>
                </div>

                <div>
                  <Label>Devotional title</Label>
                  <Input value={form.devotional_title} onChange={(e) => setForm({ ...form, devotional_title: e.target.value })} />
                </div>
                <div>
                  <Label>Reflection / devotional body</Label>
                  <textarea
                    className="mt-1 w-full min-h-48 rounded-md border border-input bg-background px-3 py-2 text-base"
                    value={form.devotional_body}
                    onChange={(e) => setForm({ ...form, devotional_body: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Encouragement</Label>
                  <textarea
                    className="mt-1 w-full min-h-28 rounded-md border border-input bg-background px-3 py-2 text-base"
                    value={form.encouragement}
                    onChange={(e) => setForm({ ...form, encouragement: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Palal</Label>
                  <textarea
                    className="mt-1 w-full min-h-36 rounded-md border border-input bg-background px-3 py-2 text-base"
                    value={form.palal}
                    onChange={(e) => setForm({ ...form, palal: e.target.value })}
                  />
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_active !== false}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                  Publish this override when its day arrives
                </label>

                <div className="flex flex-wrap gap-3">
                  <Button className="btn-gold" onClick={save}>
                    <Save className="w-4 h-4 mr-2" />
                    Save devotional plan
                  </Button>
                  <Link to={`/daily-devotional/${selected.day_of_year}`}>
                    <Button variant="outline">
                      Preview public page <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </section>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 p-8 text-center text-slate-500">Select a scheduled day.</div>
          )}
        </main>
      </div>
    </div>
  );
}
